import classNames from "classnames/bind";
import { isPlainObject } from "es-toolkit/compat";
import React, { useMemo, useState } from "react";
import {
  useController,
  ValidateResult,
  type FieldValues,
} from "react-hook-form";
import { Icon, mergeProps, Pill, TextInputBare } from "../..";
import { InputWithLabels } from "../_components/InputWithLabels";
import styles from "./ListInput.module.scss";
import type { ControlledListInputProps, ListInputProps } from "./types";

const cx = classNames.bind(styles);

const ListInput = (props: ListInputProps) => {
  const [currentTextValue, setCurrentTextValue] = useState<string>("");

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setCurrentTextValue("");
      props.onChange?.([...props.value, currentTextValue]);
    }
  };

  return (
    <div className={cx("container")}>
      <TextInputBare
        {...props}
        value={currentTextValue}
        onChange={setCurrentTextValue}
        onKeyDown={handleKeyDown}
      />
      <div className={cx("pills-container")}>
        {props.value.map((item, idx) => (
          <Pill
            key={`${item}-${idx}`}
            variant="info"
            iconRight={
              <Icon
                variant="x"
                size={14}
                className={cx("pill-icon")}
                onClick={() => {
                  props.onChange?.([
                    ...props.value.filter((_, i) => i !== idx),
                  ]);
                }}
              />
            }
          >
            {item}
          </Pill>
        ))}
      </div>
    </div>
  );
};

ListInput.displayName = "ListInput";

const ControlledListInput = <T extends FieldValues>({
  name,
  defaultValue,
  rules,
  label,
  isLabelShown,
  labelPosition,
  description,
  isRequired,
  isOptionalTextShown,
  shouldShowDescriptionInTooltip,
  isInline = false,
  isFluid = true,
  ...restProps
}: ControlledListInputProps<T>) => {
  const { isDisabled, isLoading, isBusy } = restProps;
  const { field, fieldState } = useController({
    name,
    defaultValue,
    rules: {
      ...rules,
      validate:
        rules?.validate && typeof rules.validate === "function"
          ? rules.validate
          : {
              ...(isRequired
                ? {
                    isRequired: (value: string[]): ValidateResult =>
                      value.some((item: any) =>
                        [null, undefined].includes(item)
                      )
                        ? `${typeof label === "string" ? label : "List input"} is required`
                        : undefined,
                  }
                : {}),
              ...(rules?.validate && isPlainObject(rules.validate)
                ? rules.validate
                : {}),
            },
    },
  });

  const mergedProps = useMemo(() => {
    return mergeProps(restProps, {
      onChange: field.onChange,
      onBlur: field.onBlur,
      disabled: field.disabled,
      name: field.name,
      ref: field.ref,
    });
  }, [
    restProps,
    field.onChange,
    field.onBlur,
    field.disabled,
    field.name,
    field.ref,
  ]);

  const invalidMessage = fieldState.invalid
    ? fieldState.error?.message ||
      `Input is invalid (${fieldState.error?.type})`
    : undefined;

  return (
    <InputWithLabels
      colorScheme="light"
      isDisabled={isDisabled ?? false}
      isLoading={isLoading ?? false}
      isBusy={isBusy ?? false}
      label={label}
      isLabelShown={isLabelShown}
      labelPosition={labelPosition}
      description={description}
      isRequired={isRequired}
      isOptionalTextShown={isOptionalTextShown}
      shouldShowDescriptionInTooltip={shouldShowDescriptionInTooltip}
      isInline={isInline}
      isFluid={isFluid}
      invalidMessage={invalidMessage}
    >
      {({ leftSlot, flairSlot, invalidMessageId, labelId, inputId }) => (
        <ListInput
          id={inputId}
          labelledById={labelId}
          invalidMessage={invalidMessage}
          aria-invalid={fieldState.invalid}
          aria-describedby={invalidMessageId}
          {...(!isLabelShown && !description && typeof label === "string"
            ? { "aria-label": label }
            : {})}
          leftSlot={leftSlot}
          flairSlot={flairSlot}
          {...mergedProps}
          value={field.value}
        />
      )}
    </InputWithLabels>
  );
};

ControlledListInput.displayName = "ControlledListInput";

export { ControlledListInput };
export default ListInput;
